import React from 'react';
import Form from './Generic/Form';
import { SingleInputColumn } from './Generic/InputGrouping';
import SkillInput from './SkillInput';
import { addClassNames } from '../Functions/Helper/Component';
import CliftonStrengthInput from './CliftonStrengthInput/CliftonStrengthInput';
import { XCircle as ClearIcon, Robot as AIIcon } from './Generic/Icons';
import { useAchievementGeneration } from './AchievementGenerationModal';
import Input from './Generic/Input';

const AcheivementStatementLabel = (<>
    <strong>Achievement Statement</strong> (Outcome / Benefit followed by the how - context) <span className='required-tag'>*</span> <em className='fs-smaller'>(max 500 char)</em>
</>);

const GenerateButton = ({generateAchievementStatement, className}) => (
    <button type="button" className={addClassNames("btn btn-ai-generate fs-smaller", className)} onClick={generateAchievementStatement}><AIIcon className="icon"/>Create Achievement Statement</button>
);

function AchievementStatementInput({GenerateButton, ...props}) {
    return (<>
        <div onMouseDown={console.log} onClick={console.log} className={props?.value?.length? "d-none": "d-block"}>
            <div><label>{AcheivementStatementLabel}</label></div>
            <div className="d-flex flex-column no-wrap gap-2  align-items-start my-3"><strong><small>Click the blue button below to automatically create a selection of achievement statements based on the above inputs you can choose from. (You will be able to edit your selected statement later if you wish.)</small><span className="required-tag">*</span></strong> {GenerateButton} <i className="note fs-smaller"></i></div>
        </div>
        <div className={props?.value?.length? "d-block": "d-none"}>
            <Input key="achievement-input"  {...props} type="richtext" label={
                <div className="d-flex flex-row flex-wrap justify-content-between gap-2">
                    <div>{AcheivementStatementLabel}</div>
                    <span className="mb-3">{GenerateButton}</span>
                </div>
            } />
            <br/>
            <small>Following the creation of your draft statement - ready to edit if you wish, to save your achievements, you must click on the Save button at the bottom of this screen before exiting this screen. Do not leave your screen without saving, or your draft statements may be lost.</small>
        </div>
    </>);
}
const requiredParams =  ["mcb_achievement", "mcb_benefit", "mcb_achievementstatement"];

export default function AchievementForm({ 
    value,
    symbol,
    isNew,
    onChange,
    Collapse,
    Collapsable,
    ActionComponent,
    onSubmit,
    onCancel,
    forceCallback: _forceCallback,
    formContainerRef,
    className
}) {
    const formRef = React.useRef(null);
    const generateAchievementStatement = useAchievementGeneration(value, onChange);
    const forceCallback = React.useCallback(() => {
        if (_forceCallback && (typeof _forceCallback !== "function" || _forceCallback())) {
            return true;
        }
        return value?.skills?.length > 0
        || requiredParams.some((alias) =>  value?.[alias]?.length && value[alias].trim().length > 0)
    }, [value, _forceCallback]);

    const isValid = React.useCallback((promptGenerate = true) =>{ 
        if (
            value?.skills?.length > 0
            && requiredParams.slice(0,2).every((alias) => (
                value?.[alias]?.length && value[alias].trim().length > 0
            ))
        ) {
            if (value?.[requiredParams[2]]?.length && value[requiredParams[2]].trim().length > 5) {
                return true;
            } else if (promptGenerate) {
                generateAchievementStatement({ remarks: <>Achievement Statement is <span className='required-tag'>mandatory</span>.</> });
            }
        }
        return false;
    }, [value, generateAchievementStatement]);
    
    React.useEffect(() => {
        if (!formContainerRef?.current) return;
        const formContainer = formContainerRef.current;
        if (!formContainer || typeof formContainer.getElementsByClassName !== 'function') {
            return;
        }
        const disabled = !isValid(false);

        const btn = formContainer.getElementsByClassName("submit-button")[0];
        if (btn) btn.disabled = disabled;

        const aiBtn = formContainer.getElementsByClassName("btn-ai-generate")[0];
        if (aiBtn) {
            if( value.mcb_achievement?.length > 4 && value.mcb_benefit?.length > 4) {
                aiBtn.disabled = false;
            } else {
                aiBtn.disabled = true;
                if (aiBtn.parentNode.children[2]) {
                    aiBtn.parentNode.children[2].innerText = "Please fill 'Task/Action' and 'Benefit/Result' to enable AI generation.";
                }
            }
        }
    }, [isValid, formContainerRef, value]);

    return (
        <Form
            ref={formRef}
            className={addClassNames("achievement-form d-flex flex-column gap-4 mb-4", className)}
            onSubmit={onSubmit}
            isNew={isNew}
            symbol={symbol}
            forceCallback={forceCallback}
            isValid={isValid}
        >

            {isNew && (forceCallback() && Collapsable) && ( <button
                type="button"
                className="btn btn-transparent text-danger fs-4 position-absolute t-3 r-0"
                title="Clear Form"
                onClick={() => {
                    onChange(() => ({}));
                    if (Collapsable) Collapse();
                }}
            ><ClearIcon/></button>)}
            <>
            <SingleInputColumn
                state={value}
                setState={onChange}
                inputs={[
                    [
                        'mcb_achievement', 'textarea', (
                            <span>
                                <strong>Task/Action</strong> (What I did / delivered?) <em className='fs-smaller'>(max 500 char)</em>
                            </span>
                        ), { required: true, maxCharacterCount: 500, rows: 1 }
                    ], [
                        'mcb_benefit', 'textarea', (
                            <span>
                                <strong>Benefit/Result</strong> / Outcome (Gain to the business from your achievement) <em className='fs-smaller'>(max 500 char)</em>
                            </span>
                        ), { required: true, maxCharacterCount: 500, rows: 1 }
                    ], [
                        'skills', SkillInput, (
                            <span>
                                <strong>Skills</strong> (1,2 or 3 words e.g. Leadership)
                            </span>
                        ), { required: true }
                    ], [
                        'cliftonstrengths', CliftonStrengthInput, (
                            <span>
                                <strong>Clifton Strengths (Top 10)</strong>
                            </span>
                        ), { multiselect: true }
                    ],
                     [
                        'mcb_achievementstatement', AchievementStatementInput, "", { required: true, GenerateButton: (<GenerateButton generateAchievementStatement={generateAchievementStatement} />), maxCharacterCount: 500 }
                    ]
                ]}
            />
            </>
            {ActionComponent && (
                <div key="actions">
                    <ActionComponent {...{onSubmit: () => {
                        if (formRef.current && formRef.current.reportValidity() && isValid()) {
                            return onSubmit();
                        }
                    }, onCancel, value, submitType: "button"}} />
                </div>
            )}
        </Form>
    );
}
